import React, { FunctionComponent, ReactNode } from 'react';

import { css } from '@emotion/react';

import { ThemeId } from '@styles/emotion';
import GrowthTheme, { palette } from '@styles/theme';

const DEFAULT_BORDER_RADIUS = '5px';

interface Props {
  className?: string;
  label?: string | ReactNode;
  type?: 'button' | 'submit';
  theme?: ThemeId;
  variant?: 'fill' | 'line' | 'borderless';
  width?: number | 'auto' | undefined;
  height?: number | 'auto' | undefined;
  fontSize?: number;
  fontWeight?: number;
  disabled?: boolean;
  handleClick?: any;
}

const Button: FunctionComponent<Props> = ({
  className,
  label,
  type = 'button',
  theme = 'primary',
  variant = 'fill',
  width = 'auto',
  height = 'auto',
  fontSize = 14,
  fontWeight = 400,
  disabled = false,
  handleClick,
}) => {
  const container = css({
    width: `${width === 'auto' ? 'auto' : `${width}px`}`,
    height: `${height === 'auto' ? 'auto' : `${height}px`}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    borderRadius: `${DEFAULT_BORDER_RADIUS}`,
    cursor: 'pointer',
    transition: '.2s',
    fontSize: `${fontSize}px`,
    lineHeight: `${fontSize}px`,
    fontWeight: `${fontWeight}`,
    boxSizing: 'border-box',
    padding: '10px 20px',
    '&:disabled': {
      backgroundColor: `${palette.gray2}`,
      color: `${palette.gray4}`,
      border: 'none',
      cursor: 'unset',
      '&:hover': {
        backgroundColor: `${palette.gray2}`,
        color: `${palette.gray4}`,
        cursor: 'unset',
      },
    },
  });

  const colors =
    variant === 'fill'
      ? css({
          color: `${GrowthTheme[theme].color}`,
          backgroundColor: `${GrowthTheme[theme].background}`,
          '&:hover': {
            backgroundColor: `${GrowthTheme[theme].hoverBackground}`,
            color: `${GrowthTheme[theme].hoverColor}`,
          },
          '&:active': {
            backgroundColor: `${GrowthTheme[theme].hoverBackground}`,
            color: `${GrowthTheme[theme].hoverColor}`,
          },
        })
      : variant === 'borderless'
        ? css({
            color:
              theme === 'grayscale'
                ? palette.gray6
                : `${GrowthTheme[theme].lineColor}`,
            backgroundColor: `${palette.white}`,
            '&:hover': {
              backgroundColor: `${GrowthTheme[theme].lineHoverBackground}`,
            },
            '&:active': {
              backgroundColor: `${GrowthTheme[theme].lineHoverBackground}`,
            },
          })
        : css({
            color:
              theme === 'grayscale'
                ? palette.gray6
                : `${GrowthTheme[theme].lineColor}`,
            backgroundColor: `${palette.white}`,
            border: `1px solid ${GrowthTheme[theme].lineBorderColor}`,
            '&:hover': {
              backgroundColor: `${GrowthTheme[theme].lineHoverBackground}`,
            },
            '&:active': {
              backgroundColor: `${GrowthTheme[theme].lineHoverBackground}`,
            },
          });

  return (
    <button
      className={className}
      css={[container, colors]}
      type={type}
      disabled={disabled}
      onClick={handleClick && handleClick}
    >
      {label}
    </button>
  );
};

export default Button;
